import { Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import {
  textOperators,
  equalityOperators,
  multiselectOperators,
  dateOperators
} from '../../../utils/constants/operators'
import { buildStatusColumn } from '../../fields/StatusField'

export const getCreditNoteColumns = ({ statusList = [], customers = [] }) => {
  const customerOptions = customers.map((item) => ({
    name: item.id.toString(),
    label: item.customer_name
  }))

  const mapCustomer = (id) => {
    const customer = customers.find((c) => c.id === id)
    if (!customer) return id

    return (
      <Link to='/customers/view' state={{ id, history: '/invoices' }}>
        <Typography>{customer.customer_name}</Typography>
      </Link>
    )
  }

  const mapInvoice = (id, row) => (
    <Link to='/invoices/view' state={{ id: row.invoice_id, history: '/creditNotes' }}>
      <Typography>{id}</Typography>
    </Link>
  )

  return [
    { name: 'creditNote_number', label: 'N° Avoir', isFilter: true, operators: textOperators },
    {
      name: 'customer_id',
      label: 'Client',
      isFilter: true,
      valueEditorType: 'multiselect',
      values: customerOptions,
      mapping: mapCustomer,
      operators: [...equalityOperators, ...multiselectOperators]
    },
    buildStatusColumn('creditNotes', statusList),
    { name: 'summary', label: 'Titre' },
    {
      name: 'creditNote_date',
      label: 'Date Avoir',
      isFilter: true,
      inputType: 'date',
      type: 'date',
      format: 'DD/MM/YYYY',
      operators: dateOperators
    },
    { name: 'periodYearMonth', label: 'Période', isFilter: true, operators: dateOperators },
    { name: 'total_amount_net', label: 'Montant total', type: 'currency', isFilter: false },
    {
      display: 'invoice.invoice_number',
      name: 'invoice_number',
      label: 'Facture',
      isFilter: false,
      operators: textOperators,
      mapping: mapInvoice
    },
    {
      name: 'createdDateTime',
      label: 'Date Création',
      isFilter: true,
      inputType: 'datetime-local',
      type: 'date',
      format: 'DD/MM/YYYY HH:mm:ss',
      operators: dateOperators
    },
    {
      name: 'createdBy',
      label: 'Créé Par',
      isFilter: true,
      operators: [...equalityOperators, ...multiselectOperators]
    },
    {
      name: 'lastModDateTime',
      label: 'Date Modification',
      isFilter: true,
      inputType: 'datetime-local',
      type: 'date',
      format: 'DD/MM/YYYY HH:mm:ss',
      operators: dateOperators
    },
    {
      name: 'lastModBy',
      label: 'Modifié Par',
      isFilter: true,
      operators: [...equalityOperators, ...multiselectOperators]
    }
  ]
}

export const getCreditNoteFields = (user, location, setCreditNoteLines) => [
  [
    {
      name: 'customer_id',
      label: 'Client',
      required: true,
      type: 'select',
      defaultValue: () => location.state?.context?.customer_id ?? null,
      options: {
        type: 'api',
        object: 'customers',
        value: 'customer_name',
        returnField: 'id',
        returnFields: ['id'],
        filter: () => ({ company_id: user.company_id })
      },
      sizeDivided: 2,
      fullWidth: true,
      validation: [{ rule: 'req', error: 'Client obligatoire' }]
    }
  ],
  [
    {
      name: 'creditNote_number',
      label: 'N° Avoir',
      readOnly: true,
      type: 'text',
      fullWidth: true,
      validation: [{ rule: 'req', error: 'N° Avoir obligatoire' }]
    },
    {
      name: 'invoice_id',
      label: 'N° Facture',
      required: true,
      type: 'select',
      depends: 'customer_id',
      fullWidth: true,
      options: {
        type: 'api',
        object: 'invoices',
        value: 'invoice_number',
        returnField: 'id',
        returnFields: ['id', 'customer_id'],
        filter: (data) => {
          const { customer_id, invoice_id } = data
          if (customer_id || invoice_id) {
            return {
              $or: [
                { $and: { company_id: user.company_id, status: { $nin: ['80', '81'] }, customer_id } },
                { id: invoice_id }
              ]
            }
          }
          return { company_id: user.company_id, status: { $nin: ['80', '81'] } }
        }
      },
      validation: [{ rule: 'req', error: 'N° Facture obligatoire' }]
    },
    {
      name: 'creditNote_date',
      label: 'Date',
      required: true,
      type: 'date',
      fullWidth: true,
      validation: [{ rule: 'req', error: 'Date obligatoire' }]
    }
  ],
  [
    {
      name: 'total_amount_net',
      label: 'Montant Total',
      type: 'currency',
      fullWidth: true,
      readOnly: true,
      sizeDivided: 4
    }
  ],
  [
    {
      name: 'summary',
      label: 'Titre',
      type: 'text',
      fullWidth: true,
      sizeDivided: 2
    }
  ],
  [
    {
      name: 'lines',
      label: 'Lignes',
      type: 'mytable',
      tableOptions: {
        paperStyle: { width: '100%', margin: '0 auto' },
        setDataParent: setCreditNoteLines,
        defaultSorted: 'id',
        columns: [
          { name: 'id', display: false, enableEditing: false },
          {
            name: 'description',
            label: 'Description',
            type: 'freeselect',
            enableEditing: true,
            dataId: () => 'priceBook_id',
            onChangeF: (data, priceBook, onRowDataChange) => {
              const updated = { ...data }
              if (updated.priceBook_id > -1) {
                const pb = priceBook.find((p) => p.id === updated.priceBook_id)
                if (pb) {
                  updated.unit_price = pb.unit_price
                  updated.quantity = pb.quantity
                  updated.net_amount = pb.unit_price * pb.quantity
                  updated.type = pb.type
                }
              } else {
                updated.priceBook_id = -1
                updated.unit_price = 0
                updated.quantity = 0
                updated.net_amount = 0
                updated.type = ''
              }
              onRowDataChange(updated)
            },
            optionsAPI: { api: 'priceBooks', filter: () => ({ company_id: user.company_id }) },
            size: 80,
            validation: [{ rule: 'req', error: 'Description obligatoire' }]
          },
          { name: 'quantity', label: 'Quantité', type: 'float', enableEditing: true, min: 0 },
          { name: 'unit_price', label: 'Prix unitaire', type: 'currency', enableEditing: true },
          {
            name: 'net_amount',
            label: 'Montant',
            type: 'currency',
            enableEditing: false,
            defaultValueFunction: ({ quantity, unit_price }) => {
              const value = parseFloat(quantity) * parseFloat(unit_price)
              return isNaN(value) ? 0 : value
            }
          },
          {
            name: 'type',
            label: 'Type',
            type: 'select',
            enableEditing: true,
            options: {
              type: 'data',
              data: [
                { id: 1, label: 'Vente marchandise' },
                { id: 2, label: 'Prestation services commerciales/artisanales' },
                { id: 3, label: 'Prestation services et activités libérales' }
              ],
              returnField: 'id',
              returnFields: ['id'],
              value: 'label'
            },
            validation: [{ rule: 'req', error: 'Type obligatoire' }]
          }
        ],
        actions: {
          add: true,
          edit: true,
          editDisabled: (creditNote) => creditNote.status >= 70,
          delete: { message: 'Voulez-vous vraiment supprimer la ligne' }
        },
        title: 'Lignes',
        options: { pageSize: 10, search: false, paging: false, columnsButton: true, actionsColumnIndex: -1 }
      },
      validation: [{ rule: 'req', error: 'Saisir au moins 1 ligne' }]
    }
  ]
]
